import api from "./api";
import { API_URL } from "../config";

// Enable AI - AI Assistant Service
// This service processes natural language queries and returns relevant data

// Get AI suggestions based on context
export const getAISuggestions = async (context = 'general') => {
  const suggestions = {
    general: [
      "Show me the new service order assigned to me",
      "What are the observations for my last report?",
      "Which technicians are available right now?",
      "Show me the low stock items that I need to refill"
    ],
    service_orders: [
      "Show me the new service order assigned to me",
      "Show service orders for today",
      "List pending service orders",
      "Get report for service order SO-001"
    ],
    inventory: [
      "Show me the low stock items that I need to refill",
      "Show equipment status",
      "List available equipment"
    ],
    documents: [
      "What are the observations for my last report?",
      "Search for reports",
      "Show documents for service order SO-001"
    ]
  };

  return suggestions[context] || suggestions.general;
};

// Real API call (using enable-ai backend)
export const processAIQueryAPI = async (query, sessionId = null) => {
  try {
    const response = await api.post('/api/ai-chat/sessions/query/', { 
      message: query,
      session_id: sessionId,
      context: {}
    });
    
    // Transform response to match expected format
    return {
      type: 'ai_response',
      data: response.data,
      message: response.data.response,
      session_id: response.data.session_id
    };
  } catch (error) {
    console.error('Error calling AI API:', error);
    // Extract meaningful error message from response
    const errorMsg = error.response?.data?.error
      || error.response?.data?.detail
      || error.response?.data?.message
      || error.message
      || 'Failed to get AI response';
    throw new Error(errorMsg);
  }
};
// Stream AI query with progress updates (Server-Sent Events)
export const processAIQueryStream = async (query, sessionId = null, onProgress, onComplete, onError) => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create EventSource for SSE
    const url = new URL(`${API_URL}/api/ai-chat/sessions/query-stream/`);
    
    // Make POST request manually since EventSource only supports GET
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        message: query,
        session_id: sessionId,
        context: {}
      })
    });

    if (!response.ok) {
      // Extract error message from response body if available
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error
          || errorData.detail
          || errorData.message
          || errorMessage;
      } catch (e) {
        // If response is not JSON, use default message
      }
      throw new Error(errorMessage);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentSessionId = sessionId;

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE messages
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete message in buffer
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            
            if (data.type === 'session') {
              currentSessionId = data.session_id;
            } else if (data.type === 'progress') {
              // Progress update
              if (onProgress) {
                onProgress({
                  stage: data.stage,
                  message: data.message,
                  progress: data.progress
                });
              }
            } else if (data.type === 'result') {
              // Final result
              if (onComplete) {
                onComplete({
                  type: 'ai_response',
                  data: data.data,
                  message: data.summary,
                  reasoning: data.reasoning,
                  session_id: currentSessionId
                });
              }
            } else if (data.type === 'error') {
              // Error
              if (onError) {
                onError(new Error(data.message));
              }
            }
          } catch (e) {
            console.error('Error parsing SSE message:', e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in AI streaming:', error);
    if (onError) {
      onError(error);
    }
  }
};
